"use client";

import { c as _c } from "react/compiler-runtime";
import z from "zod";
import { about } from "@data";
import axios from "@config/axios";
import { AxiosError } from "axios";
import { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { ContactSchema } from "@schemas/contact";
import { motion, useInView } from "framer-motion";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import FlyingRocket from "@components/common/flying-rocket";
import { CheckCircle, LoaderCircle, Mail, Phone, XCircle } from "lucide-react";
import { Button, Card, Description, FieldError, FieldGroup, Fieldset, Form, Input, Label, Separator, TextArea, TextField, toast, Tooltip } from "@heroui/react";
const WORD_STAGGER = 0.08;
const WORD_DURATION = 0.4;
const DURATION = 2.0;
const REPEAT_DELAY = 5.0;
const Contact = () => {
  const $ = _c(108);
  const ref = useRef(null);
  const router = useRouter();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      once: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const inView = useInView(ref, t0);
  const [onSendToast, setOnSendToast] = useState(null);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: "",
      email: "",
      message: ""
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const defaultValues = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      defaultValues,
      mode: "onTouched",
      resolver: zodResolver(ContactSchema)
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    clearErrors,
    control,
    formState,
    handleSubmit,
    reset
  } = useForm(t2);
  let t3;
  if ($[3] !== clearErrors || $[4] !== reset) {
    t3 = () => {
      clearErrors();
      reset(defaultValues);
    };
    $[3] = clearErrors;
    $[4] = reset;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const onClear = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = async data => {
      setOnSendToast(toast("Loading...", {
        description: "Please wait while we send your message!",
        indicator: <LoaderCircle className="animate-spin" />,
        isLoading: true,
        timeout: 0
      }));
      const {
        name,
        email,
        message
      } = data;
      await axios.post("/api/send", {
        name,
        email,
        message
      });
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const onSend = t4;
  let t5;
  if ($[7] !== onClear || $[8] !== onSendToast) {
    t5 = () => {
      onClear();
      if (onSendToast) {
        toast.close(onSendToast);
      }
      toast.success("Success!", {
        description: "You've successfully sent your message!",
        indicator: <CheckCircle />
      });
    };
    $[7] = onClear;
    $[8] = onSendToast;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== onSendToast) {
    t6 = error => {
      if (onSendToast) {
        toast.close(onSendToast);
      }
      toast.danger("Error!", {
        description: error?.response?.data?.message ?? "An error occurred!",
        indicator: <XCircle />
      });
    };
    $[10] = onSendToast;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = {
      mutationFn: onSend,
      onSuccess: t5,
      onError: t6
    };
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const {
    mutate,
    isPending
  } = useMutation(t7);
  let t8;
  if ($[15] !== inView) {
    t8 = "Let's Connect".split(" ").map((word, i) => <motion.span key={i} className="mr-1 inline-block" initial={{
      opacity: 0,
      x: -20
    }} animate={inView ? {
      opacity: 1,
      x: 0
    } : {}} transition={{
      duration: WORD_DURATION,
      delay: i * WORD_STAGGER,
      ease: "easeOut"
    }}>{word}</motion.span>);
    $[15] = inView;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <h2 className="text-2xl font-bold">{t8}</h2>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== mutate) {
    t10 = values => mutate(values);
    $[19] = mutate;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== handleSubmit || $[22] !== t10) {
    t11 = handleSubmit(t10);
    $[21] = handleSubmit;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  let t13;
  let t14;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      opacity: 0,
      x: -30
    };
    t13 = {
      opacity: 1,
      x: 0
    };
    t14 = {
      once: true
    };
    t15 = {
      duration: 0.4,
      ease: "easeOut"
    };
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t12 = $[24];
    t13 = $[25];
    t14 = $[26];
    t15 = $[27];
  }
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <motion.div className="bg-default flex size-16 shrink-0 items-center justify-center rounded-full" initial={t12} whileInView={t13} viewport={t14} transition={t15}><motion.div animate={{
        rotate: [0, 360],
        y: [0, -10, 0]
      }} transition={{
        duration: DURATION,
        repeat: Infinity,
        repeatType: "reverse",
        repeatDelay: REPEAT_DELAY,
        ease: "easeInOut"
      }}><Mail className="stroke-muted-foreground" size={32} /></motion.div></motion.div>;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="flex h-full w-full items-center justify-center gap-2 sm:flex-col md:flex-row lg:flex-col">{t16}<motion.div className="flex w-full flex-col sm:items-center sm:text-center md:items-start md:text-left lg:items-center lg:text-center" initial={{
        opacity: 0,
        x: -30
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.15,
        ease: "easeOut"
      }}><Fieldset.Legend className="text-sm font-semibold">Contact Me</Fieldset.Legend><Description className="text-muted-foreground w-full text-xs leading-snug whitespace-pre-line">{"I'd love to hear from you!\nFill the form & I'll get back to you soon."}</Description></motion.div></div>;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  let t19;
  let t20;
  let t21;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      opacity: 0,
      x: -30
    };
    t19 = {
      opacity: 1,
      x: 0
    };
    t20 = {
      once: true
    };
    t21 = {
      duration: 0.4,
      delay: 0.3,
      ease: "easeOut"
    };
    $[30] = t18;
    $[31] = t19;
    $[32] = t20;
    $[33] = t21;
  } else {
    t18 = $[30];
    t19 = $[31];
    t20 = $[32];
    t21 = $[33];
  }
  let t22;
  if ($[34] !== router) {
    t22 = () => router.push(`tel:${about.contact.phone}`);
    $[34] = router;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <motion.div whileHover={{
      x: [0, -4, 4, -4, 4, 0]
    }} transition={{
      duration: 0.4,
      ease: "easeInOut"
    }}><Phone /></motion.div>;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t22) {
    t24 = <Tooltip.Trigger><Button aria-label={`Call ${about.contact.phone}`} isIconOnly={true} onPress={t22} variant="ghost">{t23}</Button></Tooltip.Trigger>;
    $[37] = t22;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Tooltip.Content><Tooltip.Arrow />{about.contact.phone}</Tooltip.Content>;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== t24) {
    t26 = <Tooltip>{t24}{t25}</Tooltip>;
    $[40] = t24;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Separator className="h-6 w-0.5 md:h-0.5 md:w-6 lg:h-6 lg:w-0.5" />;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  let t28;
  if ($[43] !== router) {
    t28 = () => router.push(`mailto:${about.contact.email}`);
    $[43] = router;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  let t29;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <motion.div whileHover={{
      x: [0, -4, 4, -4, 4, 0]
    }} transition={{
      duration: 0.4,
      ease: "easeInOut"
    }}><Mail /></motion.div>;
    $[45] = t29;
  } else {
    t29 = $[45];
  }
  let t30;
  if ($[46] !== t28) {
    t30 = <Tooltip.Trigger><Button aria-label={`Email ${about.contact.email}`} isIconOnly={true} onPress={t28} variant="ghost">{t29}</Button></Tooltip.Trigger>;
    $[46] = t28;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Tooltip.Content><Tooltip.Arrow />{about.contact.email}</Tooltip.Content>;
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  let t32;
  if ($[49] !== t30) {
    t32 = <Tooltip>{t30}{t31}</Tooltip>;
    $[49] = t30;
    $[50] = t32;
  } else {
    t32 = $[50];
  }
  let t33;
  if ($[51] !== t26 || $[52] !== t32) {
    t33 = <div className="w-full flex-1 sm:w-1/3 md:w-full lg:w-1/3"><div className="flex h-full items-center justify-between gap-2 sm:flex-col md:flex-row lg:flex-col">{t17}<motion.div className="text-muted-foreground hidden text-sm sm:flex sm:items-center sm:justify-center sm:gap-1 md:flex-col lg:flex-row" initial={t18} whileInView={t19} viewport={t20} transition={t21}>{t26}{t27}{t32}</motion.div></div></div>;
    $[51] = t26;
    $[52] = t32;
    $[53] = t33;
  } else {
    t33 = $[53];
  }
  let t34;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <div className="bg-border h-0.5 w-full shrink-0 sm:h-full sm:w-0.5 md:h-0.5 md:w-full lg:h-full lg:w-0.5" />;
    $[54] = t34;
  } else {
    t34 = $[54];
  }
  let t35;
  let t36;
  let t37;
  let t38;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = {
      opacity: 0,
      x: 30
    };
    t36 = {
      opacity: 1,
      x: 0
    };
    t37 = {
      once: true
    };
    t38 = {
      duration: 0.4,
      ease: "easeOut"
    };
    $[55] = t35;
    $[56] = t36;
    $[57] = t37;
    $[58] = t38;
  } else {
    t35 = $[55];
    t36 = $[56];
    t37 = $[57];
    t38 = $[58];
  }
  let t39;
  if ($[59] !== formState?.errors?.name) {
    t39 = t40 => {
      const {
        field: t41
      } = t40;
      const {
        onBlur,
        onChange,
        ref: ref_0,
        value
      } = t41;
      return <TextField autoComplete="on" fullWidth={true} id="name" isInvalid={!!formState?.errors?.name} isRequired={true} name="name" onBlur={onBlur} onChange={onChange} ref={ref_0} validationBehavior="aria" value={value} variant="secondary"><Label>Name</Label><Input placeholder="Enter your name" /><FieldError>{formState?.errors?.name?.message}</FieldError></TextField>;
    };
    $[59] = formState?.errors?.name;
    $[60] = t39;
  } else {
    t39 = $[60];
  }
  let t40;
  if ($[61] !== control || $[62] !== t39) {
    t40 = <motion.div initial={t35} whileInView={t36} viewport={t37} transition={t38}><Controller name="name" control={control} render={t39} /></motion.div>;
    $[61] = control;
    $[62] = t39;
    $[63] = t40;
  } else {
    t40 = $[63];
  }
  let t41;
  let t42;
  let t43;
  let t44;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = {
      opacity: 0,
      x: 30
    };
    t42 = {
      opacity: 1,
      x: 0
    };
    t43 = {
      once: true
    };
    t44 = {
      duration: 0.4,
      delay: 0.1,
      ease: "easeOut"
    };
    $[64] = t41;
    $[65] = t42;
    $[66] = t43;
    $[67] = t44;
  } else {
    t41 = $[64];
    t42 = $[65];
    t43 = $[66];
    t44 = $[67];
  }
  let t45;
  if ($[68] !== formState?.errors?.email) {
    t45 = t46 => {
      const {
        field: t47
      } = t46;
      const {
        onBlur: onBlur_0,
        onChange: onChange_0,
        ref: ref_1,
        value: value_0
      } = t47;
      return <TextField autoComplete="on" fullWidth={true} id="email" isInvalid={!!formState?.errors?.email} isRequired={true} name="email" onBlur={onBlur_0} onChange={onChange_0} ref={ref_1} validationBehavior="aria" value={value_0} variant="secondary"><Label>Email</Label><Input placeholder="Enter your email" /><FieldError>{formState?.errors?.email?.message}</FieldError></TextField>;
    };
    $[68] = formState?.errors?.email;
    $[69] = t45;
  } else {
    t45 = $[69];
  }
  let t46;
  if ($[70] !== control || $[71] !== t45) {
    t46 = <motion.div initial={t41} whileInView={t42} viewport={t43} transition={t44}><Controller name="email" control={control} render={t45} /></motion.div>;
    $[70] = control;
    $[71] = t45;
    $[72] = t46;
  } else {
    t46 = $[72];
  }
  let t47;
  let t48;
  let t49;
  let t50;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = {
      opacity: 0,
      x: 30
    };
    t48 = {
      opacity: 1,
      x: 0
    };
    t49 = {
      once: true
    };
    t50 = {
      duration: 0.4,
      delay: 0.2,
      ease: "easeOut"
    };
    $[73] = t47;
    $[74] = t48;
    $[75] = t49;
    $[76] = t50;
  } else {
    t47 = $[73];
    t48 = $[74];
    t49 = $[75];
    t50 = $[76];
  }
  let t51;
  if ($[77] !== formState?.errors?.message) {
    t51 = t52 => {
      const {
        field: t53
      } = t52;
      const {
        onBlur: onBlur_1,
        onChange: onChange_1,
        ref: ref_2,
        value: value_1
      } = t53;
      return <TextField autoComplete="on" fullWidth={true} id="message" isInvalid={!!formState?.errors?.message} isRequired={true} name="message" onBlur={onBlur_1} onChange={onChange_1} ref={ref_2} validationBehavior="aria" value={value_1} variant="secondary"><Label>Message</Label><TextArea className="resize-none" placeholder="Enter your message here" rows={4} /><FieldError>{formState?.errors?.message?.message}</FieldError></TextField>;
    };
    $[77] = formState?.errors?.message;
    $[78] = t51;
  } else {
    t51 = $[78];
  }
  let t52;
  if ($[79] !== control || $[80] !== t51) {
    t52 = <motion.div initial={t47} whileInView={t48} viewport={t49} transition={t50}><Controller name="message" control={control} render={t51} /></motion.div>;
    $[79] = control;
    $[80] = t51;
    $[81] = t52;
  } else {
    t52 = $[81];
  }
  let t53;
  if ($[82] !== t40 || $[83] !== t46 || $[84] !== t52) {
    t53 = <FieldGroup>{t40}{t46}{t52}</FieldGroup>;
    $[82] = t40;
    $[83] = t46;
    $[84] = t52;
    $[85] = t53;
  } else {
    t53 = $[85];
  }
  let t54;
  let t55;
  let t56;
  let t57;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = {
      opacity: 0,
      x: 30
    };
    t55 = {
      opacity: 1,
      x: 0
    };
    t56 = {
      once: true
    };
    t57 = {
      duration: 0.4,
      delay: 0.3,
      ease: "easeOut"
    };
    $[86] = t54;
    $[87] = t55;
    $[88] = t56;
    $[89] = t57;
  } else {
    t54 = $[86];
    t55 = $[87];
    t56 = $[88];
    t57 = $[89];
  }
  const t58 = isPending ? "Sending..." : "Send";
  let t59;
  if ($[90] !== isPending) {
    t59 = <FlyingRocket show={isPending} />;
    $[90] = isPending;
    $[91] = t59;
  } else {
    t59 = $[91];
  }
  let t60;
  if ($[92] !== isPending || $[93] !== t58 || $[94] !== t59) {
    t60 = <motion.div className="w-full" initial={t54} whileInView={t55} viewport={t56} transition={t57}><Fieldset.Actions className="w-full"><Button className="w-full" isDisabled={isPending} type="submit" variant="outline">{t58}{t59}</Button></Fieldset.Actions></motion.div>;
    $[92] = isPending;
    $[93] = t58;
    $[94] = t59;
    $[95] = t60;
  } else {
    t60 = $[95];
  }
  let t61;
  if ($[96] !== t53 || $[97] !== t60) {
    t61 = <div className="flex w-full flex-col items-center justify-center gap-3 px-1 sm:w-2/3 md:w-full lg:w-2/3">{t53}{t60}</div>;
    $[96] = t53;
    $[97] = t60;
    $[98] = t61;
  } else {
    t61 = $[98];
  }
  let t62;
  if ($[99] !== t33 || $[100] !== t61) {
    t62 = <Fieldset className="flex w-full flex-col items-stretch justify-stretch gap-3 sm:flex-row md:flex-col lg:flex-row">{t33}{t34}{t61}</Fieldset>;
    $[99] = t33;
    $[100] = t61;
    $[101] = t62;
  } else {
    t62 = $[101];
  }
  let t63;
  if ($[102] !== t11 || $[103] !== t62) {
    t63 = <Card className="flex w-full flex-col items-center justify-center overflow-hidden p-0" variant="transparent"><Card.Content className="flex w-full flex-col items-center justify-center gap-0 py-5"><Form className="flex w-full flex-col items-center justify-center" onSubmit={t11}>{t62}</Form></Card.Content></Card>;
    $[102] = t11;
    $[103] = t62;
    $[104] = t63;
  } else {
    t63 = $[104];
  }
  let t64;
  if ($[105] !== t63 || $[106] !== t9) {
    t64 = <div ref={ref} className="flex w-full flex-col justify-center gap-3" id="contact">{t9}{t63}</div>;
    $[105] = t63;
    $[106] = t9;
    $[107] = t64;
  } else {
    t64 = $[107];
  }
  return t64;
};
export default Contact;